import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { VariableSelector } from './components/VariableSelector';
import { Legend, type LegendMode } from './components/Legend';
import { MunicipioPanel } from './components/MunicipioPanel';
import { MethodologyModal } from './components/MethodologyModal';
import { ListView } from './components/ListView';
import { I18nProvider, type Lang } from './lib/i18n';
import type { VariableKey } from './lib/census-variables';
import { readUrlState, writeUrlState, type ViewMode } from './lib/urlState';
import type { DataFile, MunicipioFeatureCollection, MunicipioRecord } from './types';

// Code-split MapLibre out of the initial bundle so the list view doesn't
// pay the ~800KB cost. When the user stays in list mode the whole map
// chunk never loads.
const MapView = lazy(async () => {
  const m = await import('./components/Map');
  return { default: m.MapView };
});

export default function App() {
  const initial = useMemo(() => readUrlState(window.location.search), []);
  const initialLang: Lang = initial.lang ?? 'es';

  return (
    <I18nProvider initial={initialLang} onChange={(l) => writeUrlState({ lang: l })}>
      <Shell
        initialVariable={initial.variable}
        initialGeoid={initial.municipio}
        initialView={initial.view}
        embed={initial.embed}
      />
    </I18nProvider>
  );
}

function pickInitialView(urlView: ViewMode | null): ViewMode {
  if (urlView) return urlView;
  if (typeof window === 'undefined') return 'map';
  // Narrow screens default to the list — it's the lighter, WhatsApp-ready
  // experience, matching the brief's primary mobile use case.
  return window.matchMedia('(max-width: 768px)').matches ? 'list' : 'map';
}

function Shell({
  initialVariable,
  initialGeoid,
  initialView,
  embed,
}: {
  initialVariable: VariableKey;
  initialGeoid: string | null;
  initialView: ViewMode | null;
  embed: boolean;
}) {
  const [data, setData] = useState<DataFile | null>(null);
  const [geojson, setGeojson] = useState<MunicipioFeatureCollection | null>(null);
  const [variable, setVariable] = useState<VariableKey>(initialVariable);
  const [selected, setSelected] = useState<string | null>(initialGeoid);
  const [mode, setMode] = useState<LegendMode>('continuous');
  const [methodOpen, setMethodOpen] = useState(false);
  const [view, setView] = useState<ViewMode>(() => pickInitialView(initialView));
  const [extent, setExtent] = useState<{ min: number | null; max: number | null }>({
    min: null,
    max: null,
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch('/data/municipios.json').then((r) => r.json() as Promise<DataFile>),
      // Only fetch geojson when the map view needs it — saves ~300KB on
      // list-only sessions.
      view === 'map'
        ? fetch('/data/municipios.geojson').then((r) => r.json() as Promise<MunicipioFeatureCollection>)
        : Promise.resolve<MunicipioFeatureCollection | null>(null),
    ]).then(([d, g]) => {
      if (cancelled) return;
      setData(d);
      if (g) setGeojson(g);
    });
    return () => {
      cancelled = true;
    };
  }, [view]);

  useEffect(() => {
    writeUrlState({ municipio: selected, variable, view });
  }, [selected, variable, view]);

  const records = useMemo(() => {
    const map = new Map<string, MunicipioRecord>();
    if (!data) return map;
    for (const r of data.municipios) map.set(r.GEOID, r);
    return map;
  }, [data]);

  const { rank, total } = useMemo(() => {
    if (!data || !selected)
      return { rank: null as number | null, total: data?.municipios.length ?? 0 };
    const sorted = [...data.municipios]
      .map((m) => ({
        id: m.GEOID,
        v: (m as unknown as Record<string, number | null>)[variable] ?? null,
      }))
      .filter((x) => x.v != null)
      .sort((a, b) => b.v! - a.v!);
    const idx = sorted.findIndex((x) => x.id === selected);
    return { rank: idx >= 0 ? idx + 1 : null, total: sorted.length };
  }, [data, selected, variable]);

  const onExtent = useCallback((min: number | null, max: number | null) => {
    setExtent((prev) => (prev.min === min && prev.max === max ? prev : { min, max }));
  }, []);

  const selectedRecord = selected ? records.get(selected) ?? null : null;

  if (!data) {
    return (
      <div className="grid h-screen place-items-center bg-an-paper">
        <div className="font-body text-sm text-an-muted">Cargando…</div>
      </div>
    );
  }

  return (
    <div className={['flex min-h-screen flex-col bg-an-paper', embed ? 'embed' : ''].join(' ')}>
      {!embed && (
        <Header
          onOpenMethodology={() => setMethodOpen(true)}
          view={view}
          onView={setView}
        />
      )}

      {view === 'list' ? (
        <main className="flex flex-1 flex-col">
          <ListView
            records={data.municipios}
            variable={variable}
            onVariable={setVariable}
            prWide={data.pr_wide}
            selected={selected}
            onSelect={setSelected}
            mode={mode}
            onMode={setMode}
            extent={extent}
            onExtent={onExtent}
          />
        </main>
      ) : (
        <main className="flex min-h-0 flex-1 flex-col md:flex-row">
          <section className="order-2 flex w-full flex-col gap-5 border-an-rule bg-an-white p-4 md:order-1 md:w-[280px] md:border-r">
            <VariableSelector value={variable} onChange={setVariable} />
            <Legend
              variable={variable}
              mode={mode}
              onMode={setMode}
              min={extent.min}
              max={extent.max}
            />
          </section>

          <section className="order-1 relative flex min-h-[55vh] flex-1 md:min-h-0">
            {geojson ? (
              <Suspense
                fallback={
                  <div className="grid h-full w-full place-items-center font-body text-sm text-an-muted">
                    Cargando mapa…
                  </div>
                }
              >
                <MapView
                  geojson={geojson}
                  records={records}
                  variable={variable}
                  mode={mode}
                  selectedGeoid={selected}
                  onSelect={setSelected}
                  onExtent={onExtent}
                />
              </Suspense>
            ) : (
              <div className="grid h-full w-full place-items-center font-body text-sm text-an-muted">
                Cargando mapa…
              </div>
            )}
          </section>

          <section className="order-3 w-full md:w-[380px] md:min-w-[320px]">
            <MunicipioPanel
              record={selectedRecord}
              variable={variable}
              prWide={data.pr_wide}
              rank={rank}
              total={total}
              onClose={() => setSelected(null)}
            />
          </section>
        </main>
      )}

      <Footer embed={embed} onOpenMethodology={() => setMethodOpen(true)} />
      <MethodologyModal open={methodOpen} onClose={() => setMethodOpen(false)} data={data} />
    </div>
  );
}
